/**
 * RuleCompilerPanel — "Compile coach rules" action + suggestion review
 * (Phase 3, coach-knowledge-codification epic; contract:
 * docs/RULE_COMPILER_CONTRACT.md §6).
 *
 * Rendered inside CalibrationTakesPanel, below the take list. Visible when
 * at least one bad/fail calibration take has a `reason_text`, or the
 * template has `common_mistakes` — otherwise there is nothing to compile.
 * Also stays visible if a run has already produced a non-idle status (so a
 * result computed before the coach removed their reason_text still shows).
 *
 * Flow: click "Compile coach rules" → POST kicks off an async background
 * job → poll GET .../compile-rules/status (via useRuleCompilerRun, which
 * owns the "is this a FRESH result?" state machine — see that hook's
 * docstring) → render:
 *   - running:            indeterminate progress state
 *   - failed:              coach-readable error + retry
 *   - completed, empty:    "no compilable rules found" (+ backend error text
 *                           if attached — the contract types `error` for the
 *                           `failed` status but doesn't forbid a `completed`
 *                           run from also attaching an explanatory note)
 *   - completed, non-empty: AnnotationSuggestionPanel in compiled-rule mode
 *     (§6 REUSE).
 *
 * ─── Accept is a batch through its own endpoint (PR-8, audit item RC-14) ────
 *
 * Accept used to be a whole-template `PUT`: read the annotations array, append
 * one suggestion's annotation dict, write the whole template back. One
 * suggestion per click, with a panel-wide lock in this component because two
 * accepts in flight could each read the array from before the other landed and
 * silently drop a check. Three things that path could not do, none of which is
 * a client problem:
 *
 *   · **Review a batch.** The coach committed one card at a time, with no way
 *     to see what a set of decisions would do before making them.
 *   · **Guard a duplicate.** The same check could land twice, and both copies
 *     then take marks off for the same mistake (audit item `TV-21`).
 *   · **Refuse a stale suggestion.** A suggestion built by an older
 *     materialiser was accepted on trust (owner decision D10a; plan N-7 names
 *     this endpoint as the designed chokepoint).
 *
 * `POST .../compile-rules/accept` is now that chokepoint. It is transactional,
 * and `dry_run: true` returns the same shape while writing nothing — so this
 * panel shows the coach "this will add 3 checks and skip 1", with each skip's
 * reason in the backend's own words, BEFORE anything is written (owner
 * decision D4).
 *
 * ─── Dismissals stick (PR-8; frontend-audit vision gap 7) ──────────────────
 *
 * Dismissal used to be a `useState` set, local by contract §6 on the grounds
 * that suggestions are replaced wholesale on the next compile. A reload
 * resurrected every one of them, so a coach who had already decided against
 * five suggestions decided again on every visit. `POST
 * .../compile-rules/dismiss` persists the ids; a dismissed suggestion is also
 * refused at accept time.
 *
 * ─── One refusal surface (PR-8, audit items RC-13 / FE-12) ─────────────────
 *
 * A rule the compiler refused is NOT rendered as a card here. Its one home is
 * the `RefusedRuleNotes` list on `TemplateDetails`, fed by
 * `metadata.refused_rule_details` (falling back to `refused_rule_texts`) —
 * the same page, the durable key, with the reason beside the coach's own
 * phrase. This panel only says how many there are and where to read them (see
 * `AnnotationSuggestionPanel`'s `REFUSAL_ONLY_STATUSES`). Accepting or
 * dismissing a batch refetches the template so that list reflects the run.
 */

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Spinner } from '@/components/ui';
import AnnotationSuggestionPanel from './AnnotationSuggestionPanel';
import { useRuleCompilerRun } from '@/hooks/useRuleCompiler';
import { useTemplate } from '@/hooks/useTemplates';
import { acceptCompiledRules, dismissCompiledRules } from '@/services/templateApi';
import {
  REASONS_CHANGED_PROMPT,
  REBUILD_ACTION_LABEL,
  STALE_SUGGESTIONS_PROMPT,
} from '@/utils/ruleCompilerDisplay';
import { readStaleSuggestionMessage } from '@/utils/staleSuggestion';
import type { CalibrationTake } from '@/types/calibrationTake';
import type {
  AcceptSentimentOverride,
  CompileRulesAcceptResponse,
  RefusedSuggestion,
  ValidatedSuggestion,
} from '@/types/ruleCompiler';

interface RuleCompilerPanelProps {
  templateId: string;
  /** Calibration takes for this template — already loaded by CalibrationTakesPanel. */
  takes: CalibrationTake[];
}

/** Fallback sentence for an accept that failed for a reason we have no copy for. */
const ACCEPT_FAILURE =
  "We couldn't add those checks. Nothing was changed — try again, or reload the page.";

function checkCount(n: number): string {
  return n === 1 ? '1 check' : `${n} checks`;
}

/**
 * One skipped item, in the backend's own words.
 *
 * Prefers the `notice` (a full what / why / how from the shared catalogue) and
 * falls back to `message`, which the contract guarantees is coach-facing. This
 * component composes no sentence of its own — a refusal the frontend worded
 * itself would be a second copy of copy the backend owns.
 */
function RefusedRow({ refused }: { refused: RefusedSuggestion }) {
  const notice = refused.notice;
  return (
    <li className="text-xs text-text-secondary" data-testid="accept-refused-row">
      {notice ? (
        <>
          <span className="font-medium text-text-primary">{notice.label}</span>{' '}
          {notice.what} {notice.why} {notice.how}
        </>
      ) : (
        refused.message
      )}
    </li>
  );
}

export default function RuleCompilerPanel({ templateId, takes }: RuleCompilerPanelProps) {
  // Shares the React Query cache with TemplateDetails' own useTemplate(id)
  // call — no extra network round-trip in practice, and accepting a batch
  // invalidates the same ['templates', templateId] key, so both this panel and
  // the page's refusal list pick up the run.
  const { data: template } = useTemplate(templateId);
  const queryClient = useQueryClient();

  /** Dismissals made in THIS session, on top of the ones the server holds. */
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  /** Which suggestions the coach has ticked for the batch. */
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /**
   * What the coach's badge says now, for every suggestion they have touched —
   * keyed by suggestion id.
   *
   * Held HERE, not only on the card, because the correction has to travel with
   * the batch: a card accepted as part of "Review and add 3 checks" never has
   * its own Accept clicked, so a flip kept purely in card state would be lost
   * for exactly the accepts this PR added. What is sent is filtered down to
   * REAL corrections by `sentimentOverridesFor` below.
   */
  const [sentimentValues, setSentimentValues] = useState<
    Record<string, 'positive' | 'negative'>
  >({});
  /** The dry-run answer the coach is being shown, and the ids it was asked about. */
  const [preview, setPreview] = useState<CompileRulesAcceptResponse | null>(null);
  const [previewIds, setPreviewIds] = useState<string[]>([]);
  /** The result of the committed batch, so the coach sees what landed. */
  const [committed, setCommitted] = useState<CompileRulesAcceptResponse | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  /**
   * The whole-batch stale refusal (409). Separate from `acceptError` because it
   * is not a failure — it is an answer with an action attached (D10a).
   */
  const [stalePrompt, setStalePrompt] = useState<string | null>(null);
  // Serializes accepts across the panel. The batch endpoint is transactional,
  // so this is no longer protecting against a lost write — it stops the coach
  // committing the same batch twice while the first POST is in flight.
  const [isAcceptingAny, setIsAcceptingAny] = useState(false);

  const hasReasonText = takes.some(
    (t) => (t.label === 'bad' || t.label === 'fail') && !!t.reason_text?.trim(),
  );
  const hasCommonMistakes = (template?.common_mistakes?.length ?? 0) > 0;
  const canCompile = hasReasonText || hasCommonMistakes;

  // Gate the status fetch itself on canCompile — avoids a wasted request on
  // every template page view for the (common) case of no reason_text and no
  // common_mistakes yet.
  const { data, isRunning, triggerError, trigger } = useRuleCompilerRun(templateId, {
    enabled: canCompile,
  });

  /**
   * Every dismissal we know about: the ones the server remembers, the ones
   * carried on a suggestion's own status, and the ones made in this session.
   *
   * Three sources on purpose — the server's list is authoritative across
   * reloads, and the session set covers the moment between the click and the
   * status refetch.
   */
  const serverDismissedIds = template?.metadata?.dismissed_suggestion_ids ?? null;
  const visibleSuggestions = useMemo(() => {
    const serverDismissed = new Set(serverDismissedIds ?? []);
    return (data?.suggestions ?? []).filter(
      (s) =>
        !dismissedIds.has(s.suggestion_id) &&
        !serverDismissed.has(s.suggestion_id) &&
        s.status !== 'dismissed',
    );
  }, [data?.suggestions, dismissedIds, serverDismissedIds]);

  /** Ids still on screen — a selection can never outlive the card it came from. */
  const selectableIds = useMemo(
    () =>
      visibleSuggestions
        .filter((s) => s.status === 'validated')
        .map((s) => s.suggestion_id),
    [visibleSuggestions],
  );
  const liveSelectedIds = useMemo(
    () => selectableIds.filter((id) => selectedIds.has(id)),
    [selectableIds, selectedIds],
  );

  /**
   * The coach's corrections for one batch, as the accept request wants them.
   *
   * Only a REAL correction is sent: the stored value is compared against the
   * suggestion's own classified value, so a coach who flipped a badge twice —
   * back to where it started — sends nothing, and a suggestion they never
   * touched sends nothing. `undefined` (not `{}`) when there is nothing to
   * correct, so an ordinary accept is the contract's minimal request.
   *
   * `extra` is the correction made in the SAME click as the accept, which has
   * not reached state yet; it wins for this call only. Same shape as the
   * verbal-target switch in `AnnotationSuggestionPanel`, for the same reason.
   */
  const sentimentOverridesFor = (
    ids: string[],
    extra?: Record<string, 'positive' | 'negative'>,
  ): Record<string, AcceptSentimentOverride> | undefined => {
    const merged = { ...sentimentValues, ...extra };
    const overrides: Record<string, AcceptSentimentOverride> = {};
    for (const id of ids) {
      const value = merged[id];
      if (!value) continue;
      const original = (data?.suggestions ?? []).find((s) => s.suggestion_id === id)
        ?.critique_sentiment;
      if (value === original) continue;
      overrides[id] = { critique_sentiment: value };
    }
    return Object.keys(overrides).length > 0 ? overrides : undefined;
  };

  const reasonsChanged =
    data?.reasons_changed_since_compile === true ||
    visibleSuggestions.some((s) => s.reasons_changed_since_compile === true);
  /**
   * Rules the compiler found and did not offer (`E2E-10`).
   *
   * Read from the RUN first and from the template second. The contract persists
   * them as template metadata and the status payload carries them for the run
   * in front of the coach; preferring the run means a fresh compile is never
   * described by the previous one's leftovers, and the fallback means the list
   * still reads on a page load that has no run state. Never both at once — they
   * are the same candidates, and concatenating them would show each twice.
   */
  const droppedCandidates =
    data?.dropped_candidates ?? template?.metadata?.dropped_candidates ?? [];

  if (!canCompile) {
    return null;
  }

  const resetRunState = () => {
    setSelectedIds(new Set());
    setPreview(null);
    setPreviewIds([]);
    setCommitted(null);
    setAcceptError(null);
    setStalePrompt(null);
    // A fresh compile replaces the suggestions wholesale, so corrections made
    // against the previous run's ids would be corrections to things that no
    // longer exist.
    setSentimentValues({});
  };

  const handleCompile = () => {
    setDismissedIds(new Set());
    resetRunState();
    void trigger();
  };

  /**
   * POST the batch — as a preview or as a commit.
   *
   * Returns the response, or `null` when the call did not produce one (a stale
   * 409 or a failure), so a caller that needs to know what happened to ONE
   * suggestion can read the answer rather than assume it.
   */
  const runAccept = async (
    ids: string[],
    dryRun: boolean,
    /** A correction made in the same click as this accept — see `sentimentOverridesFor`. */
    extraOverrides?: Record<string, 'positive' | 'negative'>,
  ): Promise<CompileRulesAcceptResponse | null> => {
    if (ids.length === 0) return null;
    setAcceptError(null);
    setStalePrompt(null);
    setIsAcceptingAny(true);
    try {
      // The coach's corrections go on the DRY RUN too, so the preview describes
      // the batch they are actually about to add rather than the classifier's
      // version of it.
      const result = await acceptCompiledRules(
        templateId,
        ids,
        dryRun,
        sentimentOverridesFor(ids, extraOverrides),
      );
      if (dryRun) {
        setPreview(result);
        setPreviewIds(ids);
        return result;
      }
      setCommitted(result);
      setPreview(null);
      setPreviewIds([]);
      setSelectedIds(new Set());
      // The template now carries the new annotations AND the run's refusal
      // details, which the page's one refusal surface reads. Refetch both that
      // and the suggestion list (statuses move to `accepted`).
      await queryClient.refetchQueries({ queryKey: ['templates', templateId] });
      void queryClient.invalidateQueries({ queryKey: ['compile-rules-status', templateId] });
      return result;
    } catch (err) {
      // A 409 whose detail names a stale suggestion is a meaningful answer
      // with an action, not an error to apologise for (D10a). The backend's
      // own message is preferred so the coach reads its words.
      const staleMessage = readStaleSuggestionMessage(err);
      if (staleMessage !== null) {
        setStalePrompt(staleMessage || STALE_SUGGESTIONS_PROMPT);
        return null;
      }
      setAcceptError(err instanceof Error && err.message ? err.message : ACCEPT_FAILURE);
      return null;
    } finally {
      setIsAcceptingAny(false);
    }
  };

  /**
   * One card's Accept button. Goes through the same batch endpoint with a
   * single id, so the duplicate guard and the stale refusal apply identically
   * — there is no second accept path to keep in step.
   *
   * Returns true when this suggestion was refused as a DUPLICATE, which is
   * what makes the card say "Recorded" instead of "Added".
   */
  const handleAcceptSuggestion = async (suggestion: ValidatedSuggestion): Promise<boolean> => {
    if (!suggestion.annotation) return false;
    // Extra guard alongside the disabled buttons — belt and suspenders.
    if (isAcceptingAny) return false;
    // `suggestion` is the card's copy, carrying the value its badge shows now.
    // Passing it as the same-click correction means a flip immediately followed
    // by Accept reaches the wire even if the panel's own state has not caught
    // up with the flip callback yet.
    const result = await runAccept([suggestion.suggestion_id], false,
      suggestion.critique_sentiment
        ? { [suggestion.suggestion_id]: suggestion.critique_sentiment }
        : undefined,
    );
    if (!result) return false;
    const refused = result.refused.find((r) => r.suggestion_id === suggestion.suggestion_id);
    return refused?.kind === 'duplicate';
  };

  const handleDismissSuggestion = async (suggestionId: string) => {
    await dismissCompiledRules(templateId, [suggestionId]);
    setDismissedIds((prev) => new Set([...prev, suggestionId]));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(suggestionId);
      return next;
    });
    // The server's own dismissed list is what survives a reload, so pick it up.
    void queryClient.invalidateQueries({ queryKey: ['templates', templateId] });
  };

  const toggleSelected = (suggestionId: string) => {
    setPreview(null);
    setCommitted(null);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(suggestionId)) next.delete(suggestionId);
      else next.add(suggestionId);
      return next;
    });
  };

  return (
    <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-medium text-text-secondary">Compile coach rules</p>
          <p className="text-xs text-text-muted">
            Turn the reasons you gave on flawed/disqualifying takes (and any common mistakes)
            into measurable checks. Nothing is scored until you accept a suggestion.
          </p>
        </div>
        <button
          type="button"
          onClick={handleCompile}
          disabled={isRunning}
          className="px-3 py-1.5 rounded text-xs font-medium bg-accent text-dark-base
            hover:bg-accent-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed
            flex-shrink-0"
        >
          {isRunning ? 'Compiling…' : 'Compile coach rules'}
        </button>
      </div>

      {triggerError && <Alert variant="error">{triggerError}</Alert>}

      {/*
        D10a — the whole batch was built before we changed how checks are
        built. Not an error: an answer with one action on it. Rebuilding uses
        the same button as a first compile, so there is no second "backfill"
        concept for the coach to learn.
      */}
      {stalePrompt && (
        <div data-testid="stale-suggestions-prompt">
          <Alert variant="warning">
            <div className="space-y-2">
              <p className="text-sm">{stalePrompt}</p>
              <button
                type="button"
                onClick={handleCompile}
                className="px-2.5 py-1 rounded text-xs font-semibold bg-accent text-dark-base
                  hover:bg-accent-hover transition-colors"
              >
                {REBUILD_ACTION_LABEL}
              </button>
            </div>
          </Alert>
        </div>
      )}

      {/*
        FE-7 — the coach edited what they wrote about a clip after these
        suggestions were built. The backend has flagged this since the compiler
        shipped and nothing surfaced it, so stale suggestions were presented as
        current. Informational: offer the rebuild, never trigger one.
      */}
      {!isRunning && reasonsChanged && (
        <div data-testid="reasons-changed-prompt">
          <Alert variant="info">
            <div className="space-y-2">
              <p className="text-sm">{REASONS_CHANGED_PROMPT}</p>
              <button
                type="button"
                onClick={handleCompile}
                className="px-2.5 py-1 rounded text-xs font-semibold bg-accent text-dark-base
                  hover:bg-accent-hover transition-colors"
              >
                {REBUILD_ACTION_LABEL}
              </button>
            </div>
          </Alert>
        </div>
      )}

      {acceptError && <Alert variant="error">{acceptError}</Alert>}

      {/*
        critique-polarity epic (backlog/critique-polarity-reward-penalty-
        epic.md), PR1 recompile trigger: these suggestions were compiled
        before sentiment classification existed. Informational only — the
        coach recompiles with the SAME button, there is no separate
        "backfill" action (contract §4's existing compile-rules semantics
        are reused unchanged).
      */}
      {!isRunning && data?.sentiment_missing && (
        <Alert variant="info">
          {/* "Sentiment classification (praise vs. fault)" named the system's
              own decision, not the thing the coach cares about, and "praise"
              framed a positive instruction as a compliment rather than as a
              check they can add (owner decision D3). Same vocabulary as the
              card's badge: something to do, something to avoid. */}
          We can now tell whether each rule is something to do or something to avoid.
          Run &lsquo;Compile coach rules&rsquo; again to add that to these suggestions.
        </Alert>
      )}

      {isRunning && (
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <p className="text-sm text-text-secondary">Compiling and validating coach rules…</p>
        </div>
      )}

      {!isRunning && data?.status === 'failed' && (
        <Alert variant="error">
          {data.error || 'Rule compilation failed. Please try again.'}
        </Alert>
      )}

      {!isRunning && data?.status === 'completed' && visibleSuggestions.length === 0 && (
        <div className="text-sm text-text-muted space-y-1">
          <p>No compilable rules found.</p>
          {data.error && <p className="text-xs">{data.error}</p>}
        </div>
      )}

      {/*
        E2E-10 — rules we found and did NOT offer. The two-vote agreement gate
        killed 6 of 6 rules from one coach's common mistakes and the coach saw
        nothing: not a suggestion, not a refusal, a server log line. The gate is
        deliberately not loosened (plan §F.4 — even unanimous votes can be
        unfaithful); what changes is that the coach can see what happened.
      */}
      {!isRunning && droppedCandidates.length > 0 && (
        <div className="space-y-1" data-testid="dropped-candidates">
          <p className="text-xs font-medium text-text-secondary">
            {droppedCandidates.length === 1
              ? 'We found one more thing in your words and did not offer it as a check.'
              : `We found ${droppedCandidates.length} more things in your words and did not offer them as checks.`}
          </p>
          <p className="text-xs text-text-muted">
            We read your words twice and the two readings disagreed about what to measure, so we
            did not guess. Say it again in one sentence — one body part, one direction, one
            moment — and compile again.
          </p>
          <ul className="list-none p-0 m-0 space-y-1">
            {droppedCandidates.map((candidate, i) => (
              <li
                key={`${candidate.coach_quote}-${i}`}
                className="text-xs text-text-secondary"
                data-testid="dropped-candidate-row"
              >
                &ldquo;{candidate.coach_quote}&rdquo; — {candidate.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*
        D4 — the pre-flight preview. The coach reads what a batch will do, and
        why one item will be skipped, before anything is written. The server
        wrote nothing to produce this.
      */}
      {preview && (
        <div
          className="rounded border border-accent/40 bg-accent/5 p-3 space-y-2"
          data-testid="accept-preview"
        >
          <p className="text-sm text-text-primary">
            {preview.accepted.length === 0
              ? 'None of these can be added right now.'
              : preview.refused.length === 0
                ? `This will add ${checkCount(preview.accepted.length)}.`
                : `This will add ${checkCount(preview.accepted.length)} and skip ${preview.refused.length}.`}
          </p>
          {preview.refused.length > 0 && (
            <ul className="list-none p-0 m-0 space-y-1">
              {preview.refused.map((refused) => (
                <RefusedRow key={refused.suggestion_id} refused={refused} />
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            {preview.accepted.length > 0 && (
              <button
                type="button"
                onClick={() => void runAccept(previewIds, false)}
                disabled={isAcceptingAny}
                className="px-3 py-1.5 rounded text-xs font-medium bg-accent text-dark-base
                  hover:bg-accent-hover transition-colors
                  disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isAcceptingAny ? 'Adding…' : `Add ${checkCount(preview.accepted.length)}`}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setPreview(null);
                setPreviewIds([]);
              }}
              className="px-3 py-1.5 rounded text-xs font-medium border border-border-subtle
                text-text-secondary hover:bg-dark-elevated-1 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {committed && (
        <div className="space-y-1" data-testid="accept-result">
          <p className="text-sm text-success">
            {committed.accepted.length === 0
              ? 'Nothing was added.'
              : `Added ${checkCount(committed.accepted.length)}.`}
          </p>
          {committed.refused.length > 0 && (
            <ul className="list-none p-0 m-0 space-y-1">
              {committed.refused.map((refused) => (
                <RefusedRow key={refused.suggestion_id} refused={refused} />
              ))}
            </ul>
          )}
        </div>
      )}

      {!isRunning && data?.status === 'completed' && visibleSuggestions.length > 0 && (
        <>
          {liveSelectedIds.length > 0 && !preview && (
            <button
              type="button"
              onClick={() => void runAccept(liveSelectedIds, true)}
              disabled={isAcceptingAny}
              className="px-3 py-1.5 rounded text-xs font-medium bg-accent text-dark-base
                hover:bg-accent-hover transition-colors
                disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {`Review and add ${checkCount(liveSelectedIds.length)}`}
            </button>
          )}
          <AnnotationSuggestionPanel
            compiledRuleSuggestions={visibleSuggestions}
            compiledRuleAcceptDisabled={isAcceptingAny}
            onAcceptCompiledRule={handleAcceptSuggestion}
            onDismissCompiledRule={handleDismissSuggestion}
            onCompiledRuleSentimentOverride={(suggestionId, value) => {
              // A correction changes what the batch would do, so a preview
              // taken before it is no longer the truth.
              setPreview(null);
              setSentimentValues((prev) => ({ ...prev, [suggestionId]: value }));
            }}
            compiledRuleSelection={{
              selectedIds,
              onToggle: toggleSelected,
              onSelectAll: (ids) => {
                setPreview(null);
                setCommitted(null);
                setSelectedIds(new Set(ids));
              },
              onClearSelection: () => setSelectedIds(new Set()),
            }}
          />
        </>
      )}
    </div>
  );
}
